/* JoyBundle — global behaviour.
 *
 * 承载滚动渐显和汉堡菜单两块行为，替代原先的内联脚本与内联 onclick。
 *
 * 所有监听器都委托在 `document` 上，主题编辑器重新渲染 section 时无需重新绑定。
 */
(function () {
  'use strict';

  /* ---------- Scroll reveal ------------------------------------------------
   * .fade-in 初始 opacity:0。如果只在 DOMContentLoaded 时 observe 一次，静态页面没问题，
   * 但在主题编辑器里会出事：shopify:section:load 触发得晚得多，新注入的 .fade-in 节点
   * 永远不会被 observe，section 就渲染成空白。这里用四层防护来避免。
   */
  var observer = null;

  function getObserver() {
    if (!('IntersectionObserver' in window)) return null;
    if (observer) return observer;
    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });
    return observer;
  }

  function reveal(root) {
    var scope = (root && root.querySelectorAll) ? root : document;
    var obs = getObserver();
    Array.prototype.forEach.call(
      scope.querySelectorAll('.fade-in:not(.visible)'),
      function (el) {
        if (obs) {
          obs.observe(el);
        } else {
          el.classList.add('visible');   // layer 3: no IntersectionObserver
        }
      }
    );
  }

  function revealNow(root) {
    var scope = (root && root.querySelectorAll) ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll('.fade-in'), function (el) {
      el.classList.add('visible');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { reveal(document); });
  } else {
    reveal(document);                    // defer scripts run at readyState 'interactive'
  }

  document.addEventListener('shopify:section:load', function (e) {    // layer 1
    reveal(e.target);
  });
  document.addEventListener('shopify:section:select', function (e) {  // layer 2
    revealNow(e.target);
  });

  if (window.Shopify && window.Shopify.designMode) {                  // layer 4
    window.setTimeout(function () { revealNow(document); }, 1000);
  }

  /* ---------- Mobile menu --------------------------------------------------
   * WAI-ARIA APG 的 Disclosure Navigation 模式：`.mobile-menu` 在 <header> 内部、走正常
   * 文档流把页面内容往下推，没有遮罩也没有 role=dialog —— 它是**非模态**的。
   *
   * 所以这里**刻意不做 focus trap**。非模态里锁焦点是反模式，会把键盘/读屏用户困在一段
   * 没有出口的区域里。而且不需要：DOM 顺序本来就是「汉堡按钮 → 菜单链接」，Tab 序天然正确；
   * 关闭态是 display:none，链接自动退出 Tab 序。真正需要的焦点管理只有一处 —— 按 Esc
   * 关闭时菜单变 display:none，里面的焦点会掉到 <body> 上丢失，必须显式还给汉堡按钮。
   *
   * 状态挂在 .is-open class 上，不用行内 style.display（原来的写法）。行内样式有两个坑：
   *   1. base.css 的 `.mobile-menu{display:none}` 没有媒体查询包裹，行内 display:block
   *      在**任何**宽度都压得过它 —— 在 375px 打开菜单再放宽到 ≥769px，桌面导航和移动菜单
   *      会同时显示，5 条重复链接一起重回 Tab 序。改成 class 之后，展开规则
   *      `.mobile-menu.is-open{display:block}` 只写在 base.css 的 @media (max-width:768px)
   *      里，断点之上那条规则根本不存在，class 留着也不会显示 —— 结构性消除，不需要监听 resize。
   *   2. 读状态要靠字符串比较 `style.display === 'block'`，CSS 换个 display 值就静默失效。
   */
  function menuToggle() {
    return document.querySelector('[data-menu-toggle]');
  }

  function menuIsOpen(menu) {
    return menu.classList.contains('is-open');
  }

  function setMenu(menu, open) {
    menu.classList.toggle('is-open', open);
    var toggle = menuToggle();
    if (toggle) toggle.setAttribute('aria-expanded', String(open));
  }

  document.addEventListener('click', function (e) {
    var menu = document.querySelector('[data-mobile-menu]');
    if (!menu) return;

    var toggle = e.target.closest ? e.target.closest('[data-menu-toggle]') : null;
    if (toggle) {
      setMenu(menu, !menuIsOpen(menu));
      return;
    }
    if (!menuIsOpen(menu)) return;

    /* 点在菜单内的链接上也要关：这 5 条链接全是 `/#shop` 这类同页锚点，在首页点它是同文档
       导航、**不重载**，展开状态会一直留着，菜单就卡在展开态挡在页头和目标区块之间。
       （商详页碰不到这个 —— 那里是跨文档导航会重载 —— 偏偏首页才是这些锚点真正生效的地方。）
       点在菜单内的空白处不关。 */
    var inside = e.target.closest ? e.target.closest('[data-mobile-menu]') : null;
    var link = e.target.closest ? e.target.closest('[data-mobile-menu] a') : null;
    if (inside && !link) return;

    setMenu(menu, false);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;   // 'Esc' 是旧 Edge / IE 的键名

    var menu = document.querySelector('[data-mobile-menu]');
    if (!menu || !menuIsOpen(menu)) return;

    setMenu(menu, false);

    var toggle = menuToggle();
    if (toggle) toggle.focus();   // 见顶部注释：不还焦点的话它会丢在 <body> 上
  });

  /* ---------- 账户弹层的纵向锚点 -------------------------------------------
   * <shopify-account> 的弹层是 position:fixed，按**视口**右上角定位，默认 top 是 40px
   * —— 而我们的页头下沿在 107px 上下，所以默认值会让弹层盖住页头（连刚点的那个图标一起
   * 盖住），而且它的 ::backdrop 是全透明的，底下页头照样看得见，不是「模态压暗」那种观感。
   * 组件把这个位置暴露成 --shopify-account-dialog-position-top（实测它会在这个值之上再
   * 加约 20px 间距，所以直接把页头下沿写进去，正好留出一条 20px 的缝）。
   *
   * 为什么必须现场测量而不能写死一个像素值：吸顶的是 .header-sticky，而公告栏是
   * header-group 里**不吸顶**的独立 section，于是「页头下沿的视口 Y」有两个值 —— 未滚动
   * 时 = 公告栏 + 页头，滚动之后 = 只剩页头；公告栏文案还是运营在主题编辑器里写的，
   * 375px 下会折行。任何常量都必然在某个状态下是错的。
   *
   * 为什么监听 click 而不是组件文档里写的 open 事件：click 是 composed 的，从 shadow DOM
   * 里那个按钮冒泡出来会被重定向到宿主（已实测 e.target 就是 <shopify-account>）；而
   * open / close 实测在 document 上收不到，大概率是 composed:false、出不了 shadow 边界。
   * 用捕获阶段，抢在组件自己的处理器打开弹层之前把变量写好。
   */
  document.addEventListener('click', function (e) {
    var host = e.target.closest ? e.target.closest('shopify-account') : null;
    if (!host) return;

    var sticky = document.querySelector('.header-sticky');
    if (!sticky) return;

    host.style.setProperty(
      '--shopify-account-dialog-position-top',
      Math.max(0, sticky.getBoundingClientRect().bottom) + 'px'
    );
  }, true);
})();
